<script setup lang="ts">
import { computed, onBeforeUnmount } from 'vue'

import Icon from '../../../../../components/shared/Icon.vue'

type TimeUnit = 'hours' | 'minutes'

const props = withDefaults(defineProps<{
  hours?: string,
  minutes?: string
}>(), {
  hours: '20',
  minutes: '15'
})

const emit = defineEmits<{
  'update:time': [value: { hours: string, minutes: string }],
  close: []
}>()

let swipeUnit: TimeUnit | null = null
let swipeStartY = 0
let swipeStartValue = 0

const displayHours = computed(() => String(props.hours).padStart(2, '0'))
const displayMinutes = computed(() => String(props.minutes).padStart(2, '0'))

function adjust (unit: TimeUnit, direction: number) {
  let h = parseInt(props.hours) || 0
  let m = parseInt(props.minutes) || 0

  if (unit === 'hours') {
    h = (h + direction + 24) % 24
  } else {
    // round to nearest 5-minute slot, then step
    m = Math.round(m / 5) * 5
    m = (m + direction * 5 + 60) % 60
  }

  emit('update:time', {
    hours: String(h).padStart(2, '0'),
    minutes: String(m).padStart(2, '0')
  })
}

// Swipe up/down on the hour or minute value to change it, as an alternative to the +/- buttons.
function startSwipe (event: PointerEvent, unit: TimeUnit) {
  swipeUnit = unit
  swipeStartY = event.clientY
  swipeStartValue = unit === 'hours' ? parseInt(props.hours) || 0 : parseInt(props.minutes) || 0
  window.addEventListener('pointermove', handleSwipeMove)
  window.addEventListener('pointerup', endSwipe)
  window.addEventListener('pointercancel', endSwipe)
}

function handleSwipeMove (event: PointerEvent) {
  if (!swipeUnit) return

  const stepPx = 24 // px of vertical movement per step, swipe up increases the value
  const deltaSteps = Math.round((swipeStartY - event.clientY) / stepPx)
  if (deltaSteps === 0) return

  if (swipeUnit === 'hours') {
    const h = ((swipeStartValue + deltaSteps) % 24 + 24) % 24
    emit('update:time', { hours: String(h).padStart(2, '0'), minutes: displayMinutes.value })
  } else {
    const baseRounded = Math.round(swipeStartValue / 5) * 5
    const m = ((baseRounded + deltaSteps * 5) % 60 + 60) % 60
    emit('update:time', { hours: displayHours.value, minutes: String(m).padStart(2, '0') })
  }
}

function endSwipe () {
  swipeUnit = null
  window.removeEventListener('pointermove', handleSwipeMove)
  window.removeEventListener('pointerup', endSwipe)
  window.removeEventListener('pointercancel', endSwipe)
}

onBeforeUnmount(() => {
  endSwipe()
})
</script>

<template>
  <div class="bx-epg-time-spinner">
    <div class="bx-epg-time-spinner__row">
      <div class="bx-epg-time-spinner__part"
           @pointerdown="startSwipe($event, 'hours')">
        <button type="button"
                class="bx-epg-time-spinner__btn"
                aria-label="Stunde erhöhen"
                @click="adjust('hours', 1)">
          <Icon name="arrow_pager-prev"
                class="bx-epg-time-spinner__icon bx-epg-time-spinner__icon--up" />
        </button>
        <span class="bx-epg-time-spinner__value">{{ displayHours }}</span>
        <button type="button"
                class="bx-epg-time-spinner__btn"
                aria-label="Stunde verringern"
                @click="adjust('hours', -1)">
          <Icon name="arrow_pager-prev"
                class="bx-epg-time-spinner__icon bx-epg-time-spinner__icon--down" />
        </button>
      </div>
      <span class="bx-epg-time-spinner__sep">:</span>
      <div class="bx-epg-time-spinner__part"
           @pointerdown="startSwipe($event, 'minutes')">
        <button type="button"
                class="bx-epg-time-spinner__btn"
                aria-label="Minuten erhöhen"
                @click="adjust('minutes', 1)">
          <Icon name="arrow_pager-prev"
                class="bx-epg-time-spinner__icon bx-epg-time-spinner__icon--up" />
        </button>
        <span class="bx-epg-time-spinner__value">{{ displayMinutes }}</span>
        <button type="button"
                class="bx-epg-time-spinner__btn"
                aria-label="Minuten verringern"
                @click="adjust('minutes', -1)">
          <Icon name="arrow_pager-prev"
                class="bx-epg-time-spinner__icon bx-epg-time-spinner__icon--down" />
        </button>
      </div>
    </div>
    <!-- Desktop only: confirms the picked time and collapses the popover, the time itself is already applied live -->
    <button type="button"
            class="bx-form__button bx-epg-time-spinner__submit"
            @click="$emit('close')">
      Übernehmen
    </button>
  </div>
</template>
